import { type TFunction } from "i18next";
import { Link } from "react-router";
import AvatarWithFallback from "~/molecules/AvatarWithFallback";
import MoreTooltip from "~/molecules/MoreTooltip";
import { TagsValue } from "~/molecules/Tags";
import { formatDate } from "~/helpers/date";
import { type UserSummary } from "~/api/types";
import { type AnchoringColumn } from "./AnchoringTable";

const UserCell = ({ user }: { user: UserSummary | null }) =>
  user ? (
    <AvatarWithFallback user={user} size="xs" withName showTooltip />
  ) : (
    <>-</>
  );

const TagsCell = ({ tags }: { tags: string[] }) => {
  if (tags.length === 0) return <>-</>;

  return (
    <div className="flex items-center gap-1">
      <TagsValue>{tags[0]}</TagsValue>
      <MoreTooltip data={tags} variant="tag" />
    </div>
  );
};

interface TitleRow {
  documentId: number;
  documentReleaseId: number;
  title: string;
}

export const titleColumn = (
  t: TFunction,
  tab?: string,
): AnchoringColumn<TitleRow> => ({
  key: "title",
  header: t(($) => $.anchoring.columns.title),
  cell: (row) => (
    <Link
      to={`/documents/${row.documentId}?releaseId=${row.documentReleaseId}${tab ? `&tab=${tab}` : ""}`}
      className="text-primary hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      {row.title}
    </Link>
  ),
});

interface QuizCreatorRow {
  createdBy: UserSummary;
  createdAt: string;
}

export const quizCreatorColumns = (
  t: TFunction,
): AnchoringColumn<QuizCreatorRow>[] => [
  {
    key: "createdBy",
    header: t(($) => $.anchoring.columns.createdBy),
    cell: (row) => <UserCell user={row.createdBy} />,
  },
  {
    key: "createdAt",
    header: t(($) => $.anchoring.columns.createdAt),
    cell: (row) => formatDate(row.createdAt),
  },
];

export interface ReleaseRow {
  owner: UserSummary | null;
  publisher: UserSummary | null;
  publishedAt: string | null;
  adoptedAt: string | null;
  tags: string[];
}

export const releaseColumns = (t: TFunction): AnchoringColumn<ReleaseRow>[] => [
  {
    key: "owner",
    header: t(($) => $.anchoring.columns.owner),
    cell: (row) => <UserCell user={row.owner} />,
  },
  {
    key: "publisher",
    header: t(($) => $.anchoring.columns.publisher),
    cell: (row) => <UserCell user={row.publisher} />,
  },
  {
    key: "publishedAt",
    header: t(($) => $.anchoring.columns.publishedAt),
    cell: (row) => formatDate(row.publishedAt),
  },
  {
    key: "adoptedAt",
    header: t(($) => $.anchoring.columns.adoptedAt),
    cell: (row) => formatDate(row.adoptedAt),
  },
  {
    key: "tags",
    header: t(($) => $.anchoring.columns.tags),
    cell: (row) => <TagsCell tags={row.tags} />,
  },
];
